import { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import {
  useDebounce,
  useMerchantApiQueryWithParams,
} from "../../../../../hooks";
import { IBank, NoMeta } from "../../../../../interfaces-and-types";
import { createBankSlug } from "../../../../../utils";
import { View } from "./View";

interface ICountryAndCurrencySelect {
  country: string;
  currency: string;
}

interface IBankCodesForBulkUploadProps {
  isModalOpen: boolean;
  closeModal: () => void;
}

export interface IBankCodesForBulkUploadViewProps
  extends IBankCodesForBulkUploadProps {
  downloadCsvBankCodesAndSlugs: () => void;
  setSearchBanksText: (searchText: string | null) => void;
  isLoadingBankList: boolean;
  filteredBankList: IBankWithShortCodeAndSlug[];
}

export interface IBankWithShortCodeAndSlug {
  bank_name: string;
  bank_code: string;
  slug: string;
}

export const BankCodesForBulkUpload = (props: IBankCodesForBulkUploadProps) => {
  const [banksListParams, setBanksListParams] =
    useState<ICountryAndCurrencySelect>({ country: "NG", currency: "NGN" });

  const [searchBanksText, setSearchBanksText] = useState<string | null>(null);

  const isDebounced = useDebounce(200, searchBanksText);
  const [searchBanksTextWithDebounced, setSearchBanksTextWithDebounced] =
    useState<string | null>(searchBanksText);

  useEffect(() => {
    if (isDebounced) setSearchBanksTextWithDebounced(searchBanksText);
  }, [isDebounced, searchBanksText]);

  const debouncePeriodElapsed = useDebounce();

  const {
    isSuccess: isSuccessBankList,
    isLoading: isLoadingBankList,
    data: bankList,
  } = useMerchantApiQueryWithParams<ICountryAndCurrencySelect, IBank[], NoMeta>(
    [
      "getBanks",
      {
        country: banksListParams.country,
        currency: banksListParams.currency,
      },
    ],
    {
      enabled:
        debouncePeriodElapsed &&
        !!banksListParams.country &&
        !!banksListParams.currency,
    }
  );

  const banksListWithSlugs = useMemo<IBankWithShortCodeAndSlug[]>(() => {
    return (
      bankList?.map((bank) => ({
        bank_name: bank.bank_name.toUpperCase(),
        bank_code: bank.bank_code,
        slug: createBankSlug(
          bank.bank_name,
          banksListParams.country,
          banksListParams.currency
        ),
      })) || []
    );
  }, [isSuccessBankList, banksListParams.country, banksListParams.currency]);

  const filteredBankList = (banksListWithSlugs || []).filter(
    (bank) =>
      !searchBanksTextWithDebounced ||
      bank.bank_name
        ?.toLowerCase()
        .includes(searchBanksTextWithDebounced.toLowerCase()) ||
      bank.bank_code?.includes(searchBanksTextWithDebounced)
  );

  const downloadCsvBankCodesAndSlugs = () => {
    let csv = Papa.unparse(
      banksListWithSlugs.map((bnk) => ({
        ...bnk,
        bank_code: `="${bnk.bank_code}"`,
      }))
    );

    let csvData = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    let csvURL = null;
    if (window?.URL?.createObjectURL) {
      csvURL = window.URL.createObjectURL(csvData);
    } else {
      alert(
        "Cannot download using this browser. Please switch to a different browser."
      );
      return;
    }

    var tempLink = document.createElement("a");
    tempLink.href = csvURL;
    tempLink.setAttribute("download", "switchapp-bank-codes-and-slugs.csv");
    tempLink.click();
  };

  return (
    <View
      {...props}
      downloadCsvBankCodesAndSlugs={downloadCsvBankCodesAndSlugs}
      setSearchBanksText={setSearchBanksText}
      isLoadingBankList={isLoadingBankList}
      filteredBankList={filteredBankList}
    />
  );
};
